<template>
  <div class="page-editor-layer-toolbar" v-if="components">
    <div class="layer-top-div">
      <Dropdown
        class="searchable-dropdown"
        :options=flattenArr(components)
        v-on:selected="searchSelection"
        :disabled="false"
        name="zipcode"
        :maxItem="10"
        placeholder="Please select an option">
      </Dropdown>
      <div class="show-stage-div">
        <p>Show Stage</p>
        <toggle-button
          @change="toggleStageVisible"
          :value="true"
        />
      </div>
    </div>
    <template v-if="authorize(permissibleActions.reorderable.minPermissionLevel)">
      <draggable v-model="componentsShadow" :disabled="lockInteraction" @end="dragEnd">
        <div role="tablist" v-for="component in components" :key="component.sort">
          <ul>
            <PageEditorTreeItem
              @onChange="setFocus"
              :focus="focus"
              :reload="reload"
              :userInteraction="userInteraction"
              :lockInteraction="lockInteraction"
              class="item"
              :item="component"
              :elementPath="elementPath"
              :data-uuid="component.uuid"
              :data-drag="true"
            />
          </ul>
        </div>
      </draggable>
    </template>
    <template v-else>
      <div role="tablist" v-for="component in components" :key="component.sort">
        <ul>
          <PageEditorTreeItem
            @onChange="setFocus"
            :focus="focus"
            :reload="reload"
            :userInteraction="userInteraction"
            :lockInteraction="lockInteraction"
            class="item"
            :item="component"
            :elementPath="elementPath"
            :data-uuid="component.uuid"
            :data-drag="false"
          />
        </ul>
      </div>
    </template>

  </div>
</template>

<script>
import _ from 'lodash';
import draggable from 'vuedraggable';
import Dropdown from 'vue-simple-search-dropdown';
import { DataPropertiesService } from '@/common/services/data_properties.servicejs';
import PageEditorTreeItem from './page-editor-layers/page-editor-tree-item.vue';
import EditorEvents from '../mixins/editor-events';
import PageEditorPermissionSubsystem from '../mixins/page-editor-permissions-subsystem';
import { usePageEditorStore } from '@/stores/PageEditorStore';

export default {
  name: 'PageEditorLayers',
  mixins: [EditorEvents, PageEditorPermissionSubsystem],
  components: {
    // PageEditorComponent,
    PageEditorTreeItem,
    draggable,
    Dropdown,
  },
  props: {
    components: Array,
    userInteraction: Object,
    focus: Object,
    reload: Boolean,
    lockInteraction: Boolean,
    elementPath: Array,
  },
  setup() {
    const pageEditorStore = usePageEditorStore();
    return {
      pageEditorStore,
    };
  },
  data() {
    return {
      componentsShadow: [],
      kioskId: null,
      permissionLevel: null,
    };
  },
  computed: {
    isDisabled() {
      return this.lockInteraction;
    },
  },
  watch: {
    /**
     * Reload the page, pretty clear
     */
    reload() {
      this.createShadows();
    },
  },
  created() {
    this.kioskId = this.$route.params.kioskId;
    this.createShadows();
  },
  mounted() {
    this.permissionLevel = this.pageEditorStore.getPermissionLevel;
  },
  methods: {
    /**
     * Send focus signal
     * @param object
     */
    setFocus(object) {
      this.log('setFocus object', object);
      if (this.lockInteraction) {
        return;
      }
      this.$emit('onChange', object);
    },
    /**
     * create shadow components
     */
    createShadows() {
      this.componentsShadow = this.components;
    },
    /**
     * When the drag end is done, compile the new order of shadows and send that off to update
     * the dataObjects and reorder the non shadow copy
     */
    dragEnd() {
      const updateBuffer = [];

      for (let i = 0; i < this.componentsShadow.length; i += 1) {
        const shadow = this.componentsShadow[i];

        if (shadow) {
          const obj = {
            component: shadow,
            model_order: i + 1,
            name: shadow.name,
            id: shadow.id,
          };

          updateBuffer.push(obj);
        }
      }
      this.log('updateBuffer', updateBuffer);
      for (let i = 0; i < updateBuffer.length; i += 1) {
        const updateTask = updateBuffer[i];
        const { component } = updateTask;
        if (component) {
          component.properties.model_order = updateTask.model_order;
          DataPropertiesService.update(component, this.kioskId)
            .then(data => DataPropertiesService.show(data, this.kioskId))
            .then(data => this.updateObject(data));
        }
      }
    },
    flattenArr(arr) {
      let flatArr = [];
      arr.forEach((component) => {
        component.tags.forEach((tag) => {
          if (!_.find(flatArr, { id: component.id, name: tag })) {
            flatArr.push({ id: component.id, name: `${tag} - ${component.name}`, component });
          }
        });
        if (component.children && component.children.length > 0) {
          flatArr = flatArr.concat(this.flattenArr(component.children));
        }
        if (component.parent_elements && component.parent_elements.length > 0) {
          flatArr = flatArr.concat(this.flattenArr(component.parent_elements));
        }
      });
      return flatArr;
    },
    toggleStageVisible() {
      this.pageEditorStore.toggleStageVisible();
    },
    searchSelection: _.debounce(function debounceSearch(object) {
      this.log('searchSelection', 'object', [object, typeof object]);
      const { component } = object;
      if (component && component.uuid) {
        this.setFocus(component);
      }
    }, 250),
  },
};
</script>

<style lang="scss" scoped>
.page-editor-layer-toolbar {
  min-height: 500px;
  max-height: 750px;
  overflow: auto;
  width: 100%;
  padding: 25px 0 25px 15px;
  font-size: 15px;
}
.item {
  cursor: pointer;
}
.layer-top-div {
  margin: 0 15px 20px;
  .show-stage-div {
    margin-top: 15px;
    display: flex;
    p {
      color: #0F082EFF;
      font-family: "Helvetica", sans-serif;
      font-size: 16px;
      font-weight: 400;
      margin-bottom: 0px;
      margin-right: 15px;
    }
  }
}
ul {
  margin-bottom: 0;
}
</style>
